import { computed } from 'vue';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import {
  GET_TRANSCRIPT_TEMPLATES,
  GET_TRANSCRIPT_DESIGN_OPTIONS,
  GET_TRANSCRIPT_PRESET_OPTIONS,
} from '@/graphql/transcript/queries';
import {
  CREATE_TRANSCRIPT_TEMPLATE,
  UPDATE_TRANSCRIPT_TEMPLATE,
  DELETE_TRANSCRIPT_TEMPLATE,
} from '@/graphql/transcript/mutations';

export function useTranscriptTemplates() {
  const toast = useToast();
  const confirm = useConfirm();

  const { result, loading, refetch } = useQuery(GET_TRANSCRIPT_TEMPLATES);
  const templates = computed(() => result.value?.transcriptTemplates ?? []);

  const { result: designResult, loading: designsLoading } = useQuery(GET_TRANSCRIPT_DESIGN_OPTIONS);
  const designOptions = computed(() => designResult.value?.transcriptDesignOptions ?? []);

  const { result: presetResult } = useQuery(GET_TRANSCRIPT_PRESET_OPTIONS);
  const presetOptions = computed(() => presetResult.value?.transcriptPresetOptions ?? []);

  const { mutate: createMutate, loading: creating } = useMutation(CREATE_TRANSCRIPT_TEMPLATE);
  const { mutate: updateMutate, loading: updating } = useMutation(UPDATE_TRANSCRIPT_TEMPLATE);
  const { mutate: deleteMutate } = useMutation(DELETE_TRANSCRIPT_TEMPLATE);

  async function createTemplate(input: {
    name: string;
    style_code?: string;
    properties?: Record<string, unknown>;
  }) {
    try {
      const res = await createMutate({ input });
      toast.add({
        severity: 'success',
        summary: 'Created',
        detail: `Template "${input.name}" created.`,
        life: 3000,
      });
      refetch();
      return res?.data?.createTranscriptTemplate;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to create template';
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
      throw e;
    }
  }

  async function updateTemplate(input: {
    id: string;
    name?: string;
    style_code?: string;
    properties?: Record<string, unknown>;
  }) {
    try {
      const res = await updateMutate({ input });
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: 'Template updated.',
        life: 3000,
      });
      refetch();
      return res?.data?.updateTranscriptTemplate;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update template';
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
      throw e;
    }
  }

  function confirmDelete(template: { id: string; name: string }) {
    confirm.require({
      message: `Delete template "${template.name}"? This cannot be undone.`,
      header: 'Delete Template',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Delete',
      acceptClass: 'p-button-danger',
      accept: async() => {
        try {
          await deleteMutate({ id: template.id });
          toast.add({
            severity: 'success',
            summary: 'Deleted',
            detail: `"${template.name}" deleted.`,
            life: 3000,
          });
          refetch();
        } catch (e: unknown) {
          const message = e instanceof Error ? e.message : 'Failed to delete template';
          toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
        }
      },
    });
  }

  return {
    templates,
    loading,
    refetch,
    designOptions,
    designsLoading,
    presetOptions,
    creating,
    updating,
    createTemplate,
    updateTemplate,
    confirmDelete,
  };
}
